import { Dispatch, SetStateAction } from 'react'

import useSWR from 'swr'

import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/common/api/pagination'
import {
  GetAnalysisHistoryDetailResponse,
  GetAnalysisHistoryDetailVars,
  ListAnalysisHistoryResponse,
  ListAnalysisHistoryVars,
  ListAnalysisTypesResponse,
  ListAnalysisTypesVars,
  MediaAnalysisApiPath
} from '@/common/api/params/mediaAnalysis'
import { SearchConditionBase, SearchConditionParam, useSearchCondition } from '@/common/api/search'
import { MediaAnalysisStatus } from '@/common/model/media/mediaAnalysis'

export interface MediaAnalysisHistoryCondition extends SearchConditionBase {
  type: string | null
  q: string | null
  dateFrom: string | null
  dateTo: string | null
  executionSource: string | null
  status: string | null
}

const mediaAnalysisHistoryConditionParams: SearchConditionParam[] = [
  { name: 'type', type: 'string' },
  { name: 'q', type: 'string' },
  { name: 'dateFrom', type: 'string' },
  { name: 'dateTo', type: 'string' },
  { name: 'executionSource', type: 'string' },
  { name: 'status', type: 'string' }
]

export const MEDIA_ANALYSIS_HISTORY_CONDITION_CLEAR: MediaAnalysisHistoryCondition = {
  page: DEFAULT_PAGE,
  perPage: DEFAULT_PER_PAGE,
  type: null,
  q: null,
  dateFrom: null,
  dateTo: null,
  executionSource: null,
  status: null
}

export const useMediaAnalysisHistorySearchCondition = (): [
  MediaAnalysisHistoryCondition,
  Dispatch<SetStateAction<MediaAnalysisHistoryCondition>>,
  (page: number) => void
] => {
  return useSearchCondition<MediaAnalysisHistoryCondition>(
    mediaAnalysisHistoryConditionParams,
    (paramValues) => ({ ...paramValues, perPage: DEFAULT_PER_PAGE })
  )
}

export const convertConditionToAnalysisHistoryVars = (
  siteId: number,
  condition: MediaAnalysisHistoryCondition
): ListAnalysisHistoryVars => ({
  site_id: siteId,
  page: condition.page,
  per_page: condition.perPage,
  analysis_type: condition.type || null,
  q: condition.q || null,
  date_from: condition.dateFrom ? `${condition.dateFrom}T00:00:00` : null,
  date_to: condition.dateTo ? `${condition.dateTo}T23:59:59` : null,
  execution_source: condition.executionSource || null,
  status: (condition.status as MediaAnalysisStatus) || null
})

export const useAnalysisTypesApi = (vars: ListAnalysisTypesVars) => {
  return useSWR<ListAnalysisTypesResponse, Response, [string, ListAnalysisTypesVars]>([
    MediaAnalysisApiPath.LIST_ANALYSIS_TYPES,
    vars
  ])
}

export const useAnalysisHistoryApi = (vars: ListAnalysisHistoryVars) => {
  return useSWR<ListAnalysisHistoryResponse, Response, [string, ListAnalysisHistoryVars]>([
    MediaAnalysisApiPath.LIST_ANALYSIS_HISTORY,
    vars
  ])
}

// options.refreshInterval > 0 で自動更新（分析中のポーリング用）。
// 関数形式（最新データを受け取る）も可能で、status が analyzing の間だけポーリングする用途に使う。
export const useAnalysisHistoryDetailApi = (
  vars: GetAnalysisHistoryDetailVars | undefined,
  options?: {
    refreshInterval?: number | ((latest: GetAnalysisHistoryDetailResponse | undefined) => number)
  }
) => {
  return useSWR<
    GetAnalysisHistoryDetailResponse,
    Response,
    [string, GetAnalysisHistoryDetailVars] | undefined
  >(vars ? [MediaAnalysisApiPath.GET_ANALYSIS_HISTORY_DETAIL, vars] : undefined, {
    refreshInterval: options?.refreshInterval ?? 0
  })
}
